import React, { useState } from 'react';
import { motion, Variants, AnimatePresence } from 'framer-motion';
import { FaGooglePlay, FaShieldAlt, FaChartLine, FaRegHandshake, FaMicrophoneAlt } from 'react-icons/fa';

// Placeholder images - replace these with your actual phone mockup images
import heroBgImage from '../../assets/anroidhero.jpg';
import phoneFeature1 from '../../assets/features/phone1.png';
import phoneFeature2 from '../../assets/features/phone2.png';
import phoneFeature3 from '../../assets/features/spam-caller.png';
import phoneFeature4 from '../../assets/features/phone4.png';
import phoneFeature5 from '../../assets/features/phone3.png';
import phoneFeature6 from '../../assets/features/phone1.png';
// Data for the interactive phone features
const interactiveFeatures = [
    {
        id: 1,
        title: "Effortless Payouts",
        description: "Get paid instantly. Our simplified payout system allows you to transfer your earnings directly to your bank or digital wallet.",
        image: phoneFeature1,
        alignment: "left"
    },
    {
        id: 2,
        title: "Call Analytics",
        description: "Gain valuable insights into your call data with detailed analytics. Identify top earners, call durations, and more.",
        image: phoneFeature2,
        alignment: "left"
    },
    {
        id: 3,
        title: "Smart Caller ID",
        description: "Know who's calling with our smart caller ID. Instantly identify unknown numbers and protect yourself from unwanted calls.",
        image: phoneFeature3,
        alignment: "left"
    },
    {
        id: 4,
        title: "Secure Call Shield",
        description: "Protect your personal information with advanced encryption and our smart caller ID, ensuring every call is safe and secure.",
        image: phoneFeature4,
        alignment: "right"
    },
    {
        id: 5,
        title: "Voice-to-Cash AI",
        description: "Our innovative AI transcribes calls and identifies key phrases to help you earn more. It's like having a personal assistant.",
        image: phoneFeature5,
        alignment: "right"
    },
    {
        id: 6,
        title: "Community Hub",
        description: "Connect with other users, share tips, and participate in challenges to boost your earnings and climb the leaderboards.",
        image: phoneFeature6,
        alignment: "right"
    }
];

const AndroidPage = () => {
    const [selectedFeature, setSelectedFeature] = useState(interactiveFeatures[0]);

    // Framer Motion variants for text animations
    const heroVariants: Variants = {
        hidden: { opacity: 0, y: 50 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.8,
                staggerChildren: 0.3
            }
        }
    };

    const itemVariants: Variants = {
        hidden: { opacity: 0, y: 50 },
        visible: { opacity: 1, y: 0 }
    };

    // Framer Motion variants for blinking text
    const blinkVariants: Variants = {
        blinking: {
            opacity: [0.2, 1, 0.2],
            transition: {
                duration: 1.5,
                repeat: Infinity,
                repeatType: "loop",
            }
        }
    };
    
    // Framer Motion variants for the interactive phone image
    const imageVariants: Variants = {
        initial: { opacity: 0, scale: 0.8, rotateY: 10 },
        animate: { opacity: 1, scale: 1, rotateY: 0, transition: { duration: 0.5, ease: "easeOut" } },
        exit: { opacity: 0, scale: 0.8, rotateY: -10, transition: { duration: 0.3, ease: "easeIn" } }
    };

    return (
        <div className=" text-gray-800 overflow-hidden">
            {/* Hero Section - Updated with dark, transparent background */}
            <section className="relative pt-32 pb-20 md:pt-48 md:pb-24">
                <div className="absolute inset-0 bg-black to-black z-0">
                    {/* Transparent background image */}
                    <img
                        src={heroBgImage}
                        alt="Background Pattern"
                        className="w-full h-full object-cover opacity-20"
                    />
                </div>
                <div className="container mx-auto px-6 text-center relative z-10 text-white">
                    <motion.div
                        variants={heroVariants}
                        initial="hidden"
                        animate="visible"
                    >
                        <motion.h1
                            className="text-4xl md:text-6xl font-extrabold leading-tight mb-4"
                            variants={itemVariants}
                        >
                            <span className="text-[#AEEA00]">COC</span> for Android :
                            <br className="hidden sm:inline" /> The Future of Calling
                        </motion.h1>
                        <motion.p
                            className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto mb-8"
                            variants={itemVariants}
                        >
                            Experience the most powerful and rewarding way to use your phone.
                        </motion.p>
                        <motion.div
                            className="flex flex-col items-center justify-center space-y-4"
                            variants={itemVariants}
                        >
                            <motion.span
                                className="text-3xl font-bold text-[#AEEA00]"
                                variants={blinkVariants}
                                animate="blinking"
                            >
                                Coming Soon
                            </motion.span>
                            <a
                                href="https://play.google.com/store/apps/details?id=your.app.id"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="bg-white text-[#1A237E] py-3 px-8 rounded-full font-bold shadow-lg hover:bg-gray-200 transition-colors duration-300 transform hover:scale-105"
                            >
                                <div className="flex items-center space-x-2">
                                    <FaGooglePlay className="text-2xl" />
                                    <span>Download from Google Play</span>
                                </div>
                            </a>
                        </motion.div>
                    </motion.div>
                </div>
            </section>

            {/* Interactive Phone Section */}
            <section className="py-20 md:py-24">
                <div className="container mx-auto px-6">
                    <h2 className="text-4xl md:text-5xl font-bold text-center text-[#1A237E] mb-16">
                        See Our App in Action
                    </h2>
                    <div className="grid md:grid-cols-3 gap-12 md:gap-8 items-center">
                        {/* Left Feature List */}
                        <div className="flex flex-col space-y-8">
                            {interactiveFeatures.filter(f => f.alignment === "left").map(feature => (
                                <div
                                    key={feature.id}
                                    onClick={() => setSelectedFeature(feature)}
                                    className={`p-6 rounded-lg cursor-pointer transition-all duration-300 transform ${selectedFeature.id === feature.id ? 'bg-[#AEEA00] text-[#1A237E] shadow-xl scale-105' : 'bg-gray-100 hover:bg-gray-200 text-gray-600'}`}
                                >
                                    <h3 className="text-3xl font-bold mb-2">{feature.title}</h3>
                                    <p className="text-sm">{feature.description}</p>
                                </div>
                            ))}
                        </div>

                        {/* Central Phone Display */}
<div className="flex justify-center items-center">
  <AnimatePresence mode="wait">
    <motion.div
      key={selectedFeature.id}
      variants={imageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="w-[400px] h-[500px] md:w-[400px] md:h-[600px] rounded-3xl shadow-2xl overflow-hidden flex items-center justify-center"
    >
      <img
        src={selectedFeature.image}
        alt={selectedFeature.title}
        loading="lazy"
        className="w-full h-full object-contain"
      />
    </motion.div>
  </AnimatePresence>
</div>


                        {/* Right Feature List */}
                        <div className="flex flex-col space-y-8">
                            {interactiveFeatures.filter(f => f.alignment === "right").map(feature => (
                                <div
                                    key={feature.id}
                                    onClick={() => setSelectedFeature(feature)}
                                    className={`p-6 rounded-lg cursor-pointer transition-all duration-300 transform ${selectedFeature.id === feature.id ? 'bg-[#AEEA00] text-[#1A237E] shadow-xl scale-105' : 'bg-gray-100 hover:bg-gray-200 text-gray-600'}`}
                                >
                                    <h3 className="text-3xl font-bold mb-2">{feature.title}</h3>
                                    <p className="text-sm">{feature.description}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* App Features Grid */}
            <section className="py-20 md:py-24 ">
                <div className="container mx-auto px-6">
                    <h2 className="text-4xl md:text-5xl font-bold text-center text-[#1A237E] mb-24 -mt-14">
                        Features That Redefine Your Calling Experience
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
                        {/* Feature 1 */}
                        <div className="flex flex-col items-center text-center">
                            <div className="bg-[#AEEA00] text-[#1A237E] p-6 rounded-full mb-6">
                                <FaShieldAlt className="text-4xl" />
                            </div>
                            <h3 className="text-xl font-semibold mb-2">Secure & Private</h3>
                            <p className="text-gray-600">
                                Your calls and data are protected with end-to-end encryption. Our app ensures your privacy is our top priority.
                            </p>
                        </div>

                        {/* Feature 2 */}
                        <div className="flex flex-col items-center text-center">
                            <div className="bg-[#AEEA00] text-[#1A237E] p-6 rounded-full mb-6">
                                <FaChartLine className="text-4xl" />
                            </div>
                            <h3 className="text-xl font-semibold mb-2">Real-time Earnings</h3>
                            <p className="text-gray-600">
                                Track your earnings as they happen with our intuitive dashboard. See your progress and financial growth instantly.
                            </p>
                        </div>

                        {/* Feature 3 */}
                        <div className="flex flex-col items-center text-center">
                            <div className="bg-[#AEEA00] text-[#1A237E] p-6 rounded-full mb-6">
                                <FaRegHandshake className="text-4xl" />
                            </div>
                            <h3 className="text-xl font-semibold mb-2">Effortless Payouts</h3>
                            <p className="text-gray-600">
                                Easily transfer your earnings to your bank account or digital wallet with just a few taps.
                            </p>
                        </div>

                        {/* New Feature 4 */}
                        <div className="flex flex-col items-center text-center">
                            <div className="bg-[#AEEA00] text-[#1A237E] p-6 rounded-full mb-6">
                                <FaMicrophoneAlt className="text-4xl" />
                            </div>
                            <h3 className="text-xl font-semibold mb-2">Voice-to-Text Transcription</h3>
                            <p className="text-gray-600">
                                Get a clear record of your conversations. Our app can transcribe your calls to text for easy reference and management.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

          {/* Split Box Section Centered Like NV Assistant */}
<section className="py-16 md:py-20">
  <div className=" mx-auto px-6 flex justify-center">
    <div className="flex flex-col md:flex-row items-center 
        bg-gradient-to-r from-[#3F51B5] to-[#5C6BC0] 
        rounded-3xl overflow-hidden shadow-2xl 
        w-full md:w-[1280px] h-[360px]">

      {/* Left Content */}
      <div className="w-full md:w-1/2 p-6 md:p-10 text-white">
        <h2 className="text-2xl md:text-3xl font-extrabold leading-tight mb-4">
          Achieve More with Cash On Calls
        </h2>
        <p className="text-md md:text-lg mb-6">
          Turn every incoming call into an opportunity. Track, earn, and grow
          using our powerful dashboard that simplifies your call-based income.
        </p>
        <button className="bg-white text-[#3F51B5] font-semibold px-6 py-2 rounded-full hover:bg-gray-100 transition">
          Start Earning Now
        </button>
      </div>

      {/* Right Image */}
      <div className="w-full md:w-1/2 h-full">
        <img
          src="https://techysnoop.com/wp-content/uploads/2024/07/enable-caller-id-announcement-on-android-iphone.webp"
          alt="Phone Dashboard"
          className="w-full h-full object-cover"
        />
      </div>

    </div>
  </div>
</section>


           {/* Single Image Section - Two Column Layout */}
<section className="py-20 md:py-24 ">
  <div className="container mx-auto px-6">
    <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
      
      {/* Left Image */}
      <div className="relative">
        <img
          src="https://media.idownloadblog.com/wp-content/uploads/2022/11/Compact-and-full-call-screen-iPhone-1200x675.jpg"
          alt="App Showcase"
          className="w-full h-[300px] md:h-[400px] object-cover rounded-3xl shadow-2xl"
        />
      </div>

      {/* Right Content */}
      <div className="text-left">
        <h2 className="text-4xl md:text-5xl font-extrabold text-[#1A237E] leading-tight mb-4">
          Redefining How You Connect and Earn
        </h2>
        <p className="text-lg text-gray-600 mb-6">
          Cash On Calls offers a stunning dashboard that puts all your calling analytics, earnings, and call history in one place. Designed to empower everyday users to monetize their incoming calls with ease.
        </p>
        <ul className="space-y-3 text-gray-700 font-medium">
          <li>📞 Track all your call earnings in real time</li>
          <li>📊 Visual dashboard to analyze your calling behavior</li>
          <li>🔒 100% privacy-first system, no spam or misuse</li>
        </ul>
      </div>
      
    </div>
  </div>
</section>


            {/* Final Trust Section */}
<section className="relative  py-16 md:py-20 overflow-hidden">
  <div className="container mx-auto px-6 text-center">
    <h2 className="text-3xl md:text-4xl font-extrabold text-[#1A237E] mb-4">
      Trusted by Thousands of Callers Across India
    </h2>
    <p className="text-gray-600 max-w-2xl mx-auto mb-12">
      Join 10,000+ users who are earning rewards and income just by receiving calls. Start your journey with Cash On Calls today.
    </p>

    {/* Trust Metrics */}
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
      <div className="bg-white shadow-md rounded-xl p-6 hover:shadow-xl transition duration-300">
        <h3 className="text-3xl font-bold text-indigo-800">0K+</h3>
        <p className="mt-2 text-gray-600 text-sm">Happy Call Earners</p>
      </div>
      <div className="bg-white shadow-md rounded-xl p-6 hover:shadow-xl transition duration-300">
        <h3 className="text-3xl font-bold text-indigo-800">0.0★</h3>
        <p className="mt-2 text-gray-600 text-sm">Play Store Rating</p>
      </div>
      <div className="bg-white shadow-md rounded-xl p-6 hover:shadow-xl transition duration-300">
        <h3 className="text-3xl font-bold text-indigo-800">₹0 Cr+</h3>
        <p className="mt-2 text-gray-600 text-sm">Rewards Distributed</p>
      </div>
      <div className="bg-white shadow-md rounded-xl p-6 hover:shadow-xl transition duration-300">
        <h3 className="text-3xl font-bold text-indigo-800">0%</h3>
        <p className="mt-2 text-gray-600 text-sm">Secure & Transparent</p>
      </div>
    </div>

    {/* Background Graphic or Glow */}
    <div className="absolute -bottom-10 left-1/2 transform -translate-x-1/2 w-[400px] h-[400px] bg-indigo-100 rounded-full opacity-30 blur-3xl pointer-events-none"></div>
  </div>
</section>

        </div>
    );
};

export default AndroidPage;